import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Grid, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, Box, FormControl, Select, MenuItem, Button, FormControlLabel, Switch } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { supabase } from '../supabaseClient';
import { useSecteur } from '../contexts/SecteurContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { formatDuration } from '../utils/timeUtils';
import { useTheme } from '@mui/material/styles';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const theme = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState('rolling');
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const [activityData, setActivityData] = useState({
    labels: [],
    datasets: []
  });

  const [consommationParActiviteData, setConsommationParActiviteData] = useState({
    labels: [],
    datasets: []
  });

  const [stockData, setStockData] = useState({});

  const [releveCodes, setReleveCodes] = useState([]);

  const [dataWithUsers, setDataWithUsers] = useState([]);

  const { selectedSecteur } = useSecteur();

  const [expandedCodes, setExpandedCodes] = useState({});

  const [showVariableCodes, setShowVariableCodes] = useState(false);
  const [showCalendarCodes, setShowCalendarCodes] = useState(false);

  const transformDataForChart = useCallback((data, releveCodesData) => {
    const months = [];
    const chartData = {};
    const colors = ['rgba(25, 118, 210, 0.5)', 'rgba(255, 99, 132, 0.5)', 'rgba(75, 192, 192, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)'];

    // Générer les mois selon la période sélectionnée
    if (selectedPeriod === 'rolling') {
      // 12 mois glissants - Modification de la logique
      const currentDate = new Date();
      currentDate.setDate(1); // Premier jour du mois
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push(`${monthDate.toLocaleString('fr-FR', { month: 'long' })} ${monthDate.getFullYear()}`);
      }
    } else {
      // Année spécifique - Pas de changement
      const year = parseInt(selectedPeriod);
      for (let i = 0; i < 12; i++) {
        const monthDate = new Date(year, i, 1);
        months.push(monthDate.toLocaleString('fr-FR', { month: 'long' }));
      }
    }

    // Filtrer les données selon showVariableCodes et périodicité
    const filteredData = data.filter(pointage => {
      const releveCode = releveCodesData.find(rc => rc.id === pointage.releve_code_id);
      if (!releveCode) return false;
      
      const isVariable = releveCode.particularity === 'variable';
      const isCalendar = releveCode.particularity === 'calendar';
      
      // Pour les codes calendar, vérifier si la date est dans l'année en cours
      if (isCalendar) {
        const pointageDate = new Date(pointage.date);
        const currentYear = new Date().getFullYear();
        if (pointageDate.getFullYear() !== currentYear) return false;
        return showCalendarCodes;
      }
      
      return showVariableCodes ? true : !isVariable;
    });

    // Initialiser les données pour chaque code de relève et rôle
    filteredData.forEach(pointage => {
      const pointageDate = new Date(pointage.date);
      const month = pointageDate.getMonth();
      const year = pointageDate.getFullYear();
      const releveCode = pointage.releve_codes.code;
      const role = pointage.releve_codes.role;
      const hours = pointage.hours;

      // Vérifier si le pointage correspond à la période sélectionnée
      let includePointage = false;
      if (selectedPeriod === 'rolling') {
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 11);
        startDate.setDate(1);
        includePointage = pointageDate >= startDate && pointageDate <= new Date();
      } else {
        includePointage = year.toString() === selectedPeriod;
      }

      if (includePointage) {
        const key = `${releveCode}-${role}`;
        if (!chartData[key]) {
          chartData[key] = new Array(months.length).fill(0);
        }

        let monthIndex;
        if (selectedPeriod === 'rolling') {
          monthIndex = months.indexOf(`${pointageDate.toLocaleString('fr-FR', { month: 'long' })} ${year}`);
        } else {
          monthIndex = month;
        }

        if (monthIndex !== -1) {
          chartData[key][monthIndex] += hours / 7; // Conversion en jours (7h = 1j)
        }
      }
    });

    // Trier les données avec les codes variables à la fin
    const sortedKeys = Object.keys(chartData).sort((a, b) => {
      const [codeA, roleA] = a.split('-');
      const [codeB, roleB] = b.split('-');
      const releveCodeA = releveCodesData.find(rc => rc.code === codeA && rc.role === roleA);
      const releveCodeB = releveCodesData.find(rc => rc.code === codeB && rc.role === roleB);
      
      // Si un des codes est calendar, il va avant les variables
      if (releveCodeA?.particularity === 'calendar' && releveCodeB?.particularity !== 'calendar') return -1;
      if (releveCodeA?.particularity !== 'calendar' && releveCodeB?.particularity === 'calendar') return 1;
      
      // Si un des codes est variable et l'autre non, le variable va à la fin
      if (releveCodeA?.particularity === 'variable' && releveCodeB?.particularity !== 'variable') return 1;
      if (releveCodeA?.particularity !== 'variable' && releveCodeB?.particularity === 'variable') return -1;
      
      // Si les deux sont du même type (variable ou non), trier par code puis par rôle
      return codeA.localeCompare(codeB) || roleA.localeCompare(roleB);
    });

    return {
      labels: months,
      datasets: sortedKeys.map((key) => {
        const [releveCode, role] = key.split('-');
        const releveCodeData = releveCodesData.find(code => code.code === releveCode && code.role === role);
        const backgroundColor = releveCodeData ? releveCodeData.color : colors[sortedKeys.indexOf(key) % colors.length];
        return {
          label: `${releveCode} (${role})`,
          data: chartData[key],
          backgroundColor: backgroundColor,
        };
      }),
    };
  }, [selectedPeriod, showVariableCodes, showCalendarCodes]);

  const calculateConsommationParActiviteChart = useCallback((data, dataWithUsers) => {
    const months = [];
    const chartData = {};

    // Générer les mois selon la période sélectionnée
    if (selectedPeriod === 'rolling') {
      const currentDate = new Date();
      currentDate.setDate(1); // Premier jour du mois
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push(`${monthDate.toLocaleString('fr-FR', { month: 'long' })} ${monthDate.getFullYear()}`);
      }
    } else {
      const year = parseInt(selectedPeriod);
      for (let i = 0; i < 12; i++) {
        const monthDate = new Date(year, i, 1);
        months.push(monthDate.toLocaleString('fr-FR', { month: 'long' }));
      }
    }

    // Initialiser les données pour chaque activité
    data.forEach(pointage => {
      const pointageDate = new Date(pointage.date);
      pointageDate.setDate(1); // Normaliser au premier jour du mois
      const month = pointageDate.getMonth();
      const year = pointageDate.getFullYear();
      const activityName = pointage.activity && pointage.activity.name ? pointage.activity.name : 'Inconnue';
      const hours = pointage.hours;

      // Vérifier si le pointage correspond à la période sélectionnée
      let includePointage = false;
      if (selectedPeriod === 'rolling') {
        const currentDate = new Date();
        currentDate.setDate(1);
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 11, 1);
        includePointage = pointageDate >= startDate && pointageDate <= currentDate;
      } else {
        includePointage = year.toString() === selectedPeriod;
      }

      if (includePointage) {
        if (!chartData[activityName]) {
          chartData[activityName] = new Array(months.length).fill(0);
        }

        let monthIndex;
        if (selectedPeriod === 'rolling') {
          monthIndex = months.indexOf(`${pointageDate.toLocaleString('fr-FR', { month: 'long' })} ${year}`);
        } else {
          monthIndex = month;
        }

        if (monthIndex !== -1) {
          chartData[activityName][monthIndex] += hours / 7; // Conversion en jours
        }
      }
    });

    // Transformer en format pour le graphique
    return {
      labels: months,
      datasets: Object.keys(chartData).map((activityName) => {
        // Trouver la première occurrence de l'activité pour obtenir sa couleur
        const activity = data.find(p => p.activity?.name === activityName)?.activity;
        return {
          label: activityName,
          data: chartData[activityName],
          backgroundColor: activity?.color || '#757575', // Utiliser gris par défaut si pas de couleur
        };
      }),
    };
  }, [selectedPeriod]);

  const calculateStockTable = useCallback((releveCodesData, pointages, activeMandates) => {
    const stockTable = {};
    const currentDate = new Date();

    // Déterminer la période de calcul selon la période sélectionnée
    let startDate, endDate;
    if (selectedPeriod === 'rolling') {
      const currentYear = currentDate.getFullYear();
      startDate = new Date(currentYear, 0, 1);
      endDate = new Date(currentYear, 11, 31);
    } else {
      const year = parseInt(selectedPeriod);
      startDate = new Date(year, 0, 1);
      endDate = new Date(year, 11, 31);
    }

    // Récupérer tous les mandats actifs pour initialiser les utilisateurs par code
    // On suppose que dataWithUsers contient tous les pointages, donc on extrait les mandats à partir de là
    // Pour plus de robustesse, il faudrait passer la liste des mandats à cette fonction
    // Ici, on reconstruit la liste des mandats à partir des pointages
    const allMandates = {};
    pointages.forEach(pointage => {
      const releveCode = releveCodesData.find(rc => rc.id === pointage.releve_code_id);
      if (!releveCode) return;
      const code = releveCode.code;
      const role = releveCode.role;
      const key = `${code}-${role}`;
      const userId = pointage.user_mandates.user_id;
      const fullName = pointage.users.full_name;
      if (!allMandates[key]) allMandates[key] = {};
      allMandates[key][userId] = fullName;
    });

    // Initialiser la structure pour chaque code et chaque utilisateur ayant un mandat actif
    releveCodesData.forEach(releveCode => {
      const code = releveCode.code;
      const role = releveCode.role;
      const key = `${code}-${role}`;
      if (!stockTable[key]) {
        stockTable[key] = {
          dotation: 0,
          consommation: 0,
          stock: 0,
          users: {},
          particularity: releveCode.particularity,
        };
      }
      // Initialiser tous les utilisateurs ayant un mandat actif pour ce code
      if (allMandates[key]) {
        Object.entries(allMandates[key]).forEach(([userId, fullName]) => {
          if (!stockTable[key].users[userId]) {
            stockTable[key].users[userId] = {
              consommation: 0,
              dotation: releveCode.hours_per_month || 0,
              full_name: fullName,
              particularity: releveCode.particularity,
            };
            // Dotation globale - même traitement pour tous les types de codes
            stockTable[key].dotation += releveCode.hours_per_month || 0;
          }
        });
      }
    });

    // Ajouter la consommation réelle pour chaque pointage
    pointages.forEach(pointage => {
      const releveCode = releveCodesData.find(rc => rc.id === pointage.releve_code_id);
      if (!releveCode) return;
      const pointageDate = new Date(pointage.date);
      const code = releveCode.code;
      const role = releveCode.role;
      const key = `${code}-${role}`;
      const userId = pointage.user_mandates.user_id;

      // Initialiser l'utilisateur si jamais il n'est pas déjà présent (sécurité)
      if (!stockTable[key].users[userId]) {
        stockTable[key].users[userId] = {
          consommation: 0,
          dotation: releveCode.hours_per_month || 0,
          full_name: pointage.users.full_name,
          particularity: releveCode.particularity,
        };
        // Dotation globale - même traitement pour tous les types de codes
        stockTable[key].dotation += releveCode.hours_per_month || 0;
      }

      // Add consumption based on particularity
      if (releveCode.particularity === 'monthly') {
        if (pointageDate.getMonth() === currentDate.getMonth() &&
            pointageDate.getFullYear() === currentDate.getFullYear()) {
          stockTable[key].users[userId].consommation += pointage.hours;
          stockTable[key].consommation += pointage.hours;
        }
      } else {
        // Même traitement pour calendar et autres types (sauf monthly)
        if (pointageDate >= startDate && pointageDate <= endDate) {
          stockTable[key].users[userId].consommation += pointage.hours;
          stockTable[key].consommation += pointage.hours;
        }
      }
    });

    // Initialiser les entrées pour tous les mandats actifs
    activeMandates.forEach(mandate => {
      if (!mandate.releve_codes) return;
      
      const code = mandate.releve_codes.code;
      const role = mandate.releve_codes.role;
      const key = `${code}-${role}`;
      
      if (!stockTable[key]) {
        stockTable[key] = {
          dotation: 0,
          consommation: 0,
          stock: 0,
          users: {},
          particularity: mandate.releve_codes.particularity,
        };
      }

      // Initialiser l'utilisateur même s'il n'a pas de pointage
      if (!stockTable[key].users[mandate.user_id]) {
        stockTable[key].users[mandate.user_id] = {
          consommation: 0,
          dotation: mandate.releve_codes.hours_per_month || 0,
          full_name: mandate.users.full_name,
          particularity: mandate.releve_codes.particularity,
        };
        
        // Dotation globale - même traitement pour tous les types de codes
        stockTable[key].dotation += mandate.releve_codes.hours_per_month || 0;
      }
    });

    // Calculer les stocks finaux
    Object.keys(stockTable).forEach(key => {
      stockTable[key].stock = stockTable[key].dotation - stockTable[key].consommation;
      Object.keys(stockTable[key].users).forEach(userId => {
        const user = stockTable[key].users[userId];
        user.stock = user.dotation - user.consommation;
      });
    });

    return stockTable;
  }, [selectedPeriod]);

  const fetchActivityData = useCallback(async () => {
    try {
      if (!selectedSecteur) {
        console.warn('Aucun secteur sélectionné');
        return;
      }

      // Calculer la date de début et de fin selon la période sélectionnée
      let startDate, endDate;
      
      if (selectedPeriod === 'rolling') {
        const currentDate = new Date();
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1);
      } else {
        const year = parseInt(selectedPeriod);
        startDate = new Date(year, 0, 1);
        endDate = new Date(year, 11, 31);
      }

      // Étape 1: Charger tous les mandats actifs du secteur avec leurs codes de relève
      const { data: mandates, error: mandateError } = await supabase
        .from('user_mandates')
        .select(`
          id, 
          user_id,
          releve_codes (
            id,
            code,
            hours_per_month,
            color,
            particularity,
            role
          ),
          users (
            id,
            full_name
          )
        `)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (mandateError) throw mandateError;

      const releveCodes = mandates
        .map(m => m.releve_codes)
        .filter(Boolean);

      setReleveCodes(releveCodes);

      // Étape 2: Charger les pointages pour la période
      const { data: pointages, error: pointagesError } = await supabase
        .from('pointage')
        .select(`
          id,
          date,
          hours,
          releve_code_id,
          releve_codes (code, role),
          user_mandate_id,
          user_mandates (user_id),
          activity (name, color),
          description,
          on_report
        `)
        .in('user_mandate_id', mandates.map(m => m.id))
        .gte('date', startDate.toISOString())
        .lte('date', endDate.toISOString());

      if (pointagesError) throw pointagesError;

      // Combiner les données de pointage avec les infos des utilisateurs
      const dataWithUsers = pointages.map(pointage => {
        const mandate = mandates.find(m => m.id === pointage.user_mandate_id);
        return {
          ...pointage,
          users: mandate?.users || { full_name: 'Inconnu' }
        };
      });

      setDataWithUsers(dataWithUsers);

      // Calculer les stocks en prenant en compte tous les mandats actifs
      const stockTable = calculateStockTable(releveCodes, dataWithUsers, mandates);
      setStockData(stockTable);

      // Transformer les données pour les graphiques
      const formattedData = transformDataForChart(dataWithUsers, releveCodes);
      setActivityData(formattedData);

      const consommationParActiviteChart = calculateConsommationParActiviteChart(pointages, dataWithUsers);
      setConsommationParActiviteData(consommationParActiviteChart);

    } catch (error) {
      console.error('Erreur lors de la récupération des données d activité:', error);
    }
  }, [selectedSecteur, selectedPeriod, transformDataForChart, calculateConsommationParActiviteChart, calculateStockTable]);

  const exportToPDF = async () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const faitsMarquantsElement = document.getElementById('faits-marquants');
      const graphiqueElement = document.getElementById('panel-graphiques');
  
      if (!faitsMarquantsElement || !graphiqueElement) {
        console.error('Elements not found');
        return;
      }
  
      // Configuration des marges et dimensions
      const margin = 10;
      const pageWidth = doc.internal.pageSize.getWidth();
      const maxWidth = pageWidth - (margin * 2);
  
      // Capture et ajout des graphiques
      const graphiqueCanvas = await html2canvas(graphiqueElement, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      const graphiqueImgData = graphiqueCanvas.toDataURL('image/png');
      
      // Calculer la hauteur proportionnelle pour maintenir le ratio
      const graphiqueAspectRatio = graphiqueCanvas.height / graphiqueCanvas.width;
      const graphiqueHeight = maxWidth * graphiqueAspectRatio;
  
      doc.addImage(graphiqueImgData, 'PNG', margin, margin, maxWidth, graphiqueHeight);
  
      // Ajouter une nouvelle page pour les faits marquants
      doc.addPage();
  
      // Capture et ajout des faits marquants
      const faitsMarquantsCanvas = await html2canvas(faitsMarquantsElement, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      const faitsMarquantsImgData = faitsMarquantsCanvas.toDataURL('image/png');
      
      const faitsMarquantsAspectRatio = faitsMarquantsCanvas.height / faitsMarquantsCanvas.width;
      const faitsMarquantsHeight = maxWidth * faitsMarquantsAspectRatio;
  
      doc.addImage(faitsMarquantsImgData, 'PNG', margin, margin, maxWidth, faitsMarquantsHeight);
  
      doc.save('rapport_activite.pdf');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    }
  };

  const handleToggleCode = (key) => {
    setExpandedCodes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  useEffect(() => {
    if (dataWithUsers.length > 0 && releveCodes.length > 0) {
      const formattedData = transformDataForChart(dataWithUsers, releveCodes);
      setActivityData(formattedData);
    }
  }, [dataWithUsers, releveCodes, transformDataForChart]); // Ajouter showVariableCodes et showCalendarCodes comme dépendances

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* En-tête avec contrôles */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2 
            }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
                Tableau de bord
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                alignItems: 'center',
                flexWrap: 'wrap' 
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  '& .MuiFormControlLabel-root': { 
                    marginRight: 1,
                    marginLeft: 0 
                  }
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showCalendarCodes}
                        onChange={(e) => setShowCalendarCodes(e.target.checked)}
                      />
                    }
                    label="Codes calendaires"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showVariableCodes}
                        onChange={(e) => setShowVariableCodes(e.target.checked)}
                      />
                    }
                    label="Codes variables"
                  />
                </Box>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="rolling">12 mois glissants</MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button 
                  variant="contained"
                  onClick={exportToPDF}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ 
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 1 }
                  }}
                >
                  Rapport d'activité
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Section des soldes et KPIs */}
        <Grid item xs={12}>
          {/* Codes non variables */}
          <Grid container spacing={1}>
            {Object.keys(stockData)
              .filter(key => {
                const [code, role] = key.split('-');
                const releveCode = releveCodes.find(r => r.code === code && r.role === role);
                return releveCode?.particularity !== 'variable' && releveCode?.particularity !== 'calendar';
              })
              .map((key, index) => {
                const [code, role] = key.split('-');
                const data = stockData[key];
                const color = (releveCodes.find(r => r.code === code && r.role === role) || {}).color;

                return (
                  <Grid item xs={12} md={4} lg={3} key={index}>
                    <Paper sx={{ 
                      p: 1.5,
                      height: '100%',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        height: '100%',
                      }}>
                        {/* En-tête avec le code et solde */}
                        <Box sx={{ 
                          p: 1.5,
                          backgroundColor: color + '15',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          position: 'relative',
                        }}>
                          {/* Code et rôle */}
                          <Box>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 700,
                                color: color,
                                textShadow: '0px 1px 2px rgba(0,0,0,0.1)',
                                fontSize: '1.3rem',
                              }}
                            >
                              {code}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                display: 'block',
                                mt: -0.5
                              }}
                            >
                              {role}
                            </Typography>
                          </Box>

                          {/* Espace flexible */}
                          <Box sx={{ flex: 1 }} />

                          {/* Solde */}
                          <Box sx={{ 
                            textAlign: 'right', 
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography 
                              variant="h5"
                              sx={{ 
                                fontWeight: 700,
                                color: data.stock >= 0 ? theme.palette.info.main : theme.palette.error.main,
                                fontSize: '1.6rem',
                                lineHeight: 1
                              }}
                            >
                              {formatDuration(data.stock)}
                            </Typography>
                          </Box>

                          {/* Bouton détails */}
                          <Button 
                            size="small" 
                            onClick={() => handleToggleCode(key)}
                            sx={{ 
                              minWidth: 'auto',
                              p: '4px 8px',
                              backgroundColor: 'background.paper',
                              '&:hover': {
                                backgroundColor: 'background.default'
                              }
                            }}
                          >
                            {expandedCodes[key] ? 'Réduire' : 'Détails'}
                          </Button>
                        </Box>

                        {/* Section dépliable avec dotation, consommation et détails */}
                        {expandedCodes[key] && (
                          <Box sx={{ mt: 2 }}>
                            {/* Dotation et consommation */}
                            <Box sx={{ 
                              display: 'flex',
                              gap: 2,
                              mb: 2,
                              p: 1.5,
                              backgroundColor: theme.palette.background.default,
                              borderRadius: 1
                            }}>
                              {[
                                { 
                                  label: 'Dotation', 
                                  value: formatDuration(data.dotation),
                                  color: theme.palette.success.main,
                                },
                                { 
                                  label: 'Consommation', 
                                  value: formatDuration(data.consommation),
                                  color: theme.palette.warning.main,
                                }
                              ].map((item, i) => (
                                <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                                  <Typography 
                                    variant="caption" 
                                    display="block" 
                                    sx={{ 
                                      color: item.color,
                                      fontWeight: 500
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      fontWeight: 600,
                                      color: item.color
                                    }}
                                  >
                                    {item.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* Table des détails */}
                            <Table 
                              size="small" 
                              sx={{ 
                                '& td, & th': { 
                                  padding: '6px 8px', 
                                  fontSize: '0.85rem',
                                  borderColor: theme.palette.divider
                                },
                                '& th': {
                                  backgroundColor: theme.palette.background.default,
                                  fontWeight: 600
                                },
                                '& tr:nth-of-type(even)': {
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Membre</TableCell>
                                  <TableCell align="right">Dot.</TableCell>
                                  <TableCell align="right">Cons.</TableCell>
                                  <TableCell align="right">Solde</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(data.users).map(([userId, userData], i) => (
                                  <TableRow key={i}>
                                    <TableCell>{userData.full_name}</TableCell>
                                    <TableCell align="right">{formatDuration(userData.dotation)}</TableCell>
                                    <TableCell align="right">{formatDuration(userData.consommation)}</TableCell>
                                    <TableCell align="right">{formatDuration(userData.stock)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>

          {/* Section des codes calendar */}
          <Box sx={{ mt: 2 }}>
            {showCalendarCodes && (
              <Grid container spacing={1}>
                {Object.keys(stockData)
                  .filter(key => {
                    const [code, role] = key.split('-');
                    const releveCode = releveCodes.find(r => r.code === code && r.role === role);
                    return releveCode?.particularity === 'calendar';
                  })
                  .map((key, index) => {
                    const [code, role] = key.split('-');
                    const data = stockData[key];
                    const color = (releveCodes.find(r => r.code === code && r.role === role) || {}).color;

                    return (
                      <Grid item xs={12} md={4} lg={3} key={index}>
                        <Paper sx={{ 
                          p: 1.5,
                          height: '100%',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            height: '100%',
                          }}>
                            {/* En-tête avec le code et solde */}
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: color + '15',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              position: 'relative',
                            }}>
                              {/* Code et rôle */}
                              <Box>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: color,
                                    textShadow: '0px 1px 2px rgba(0,0,0,0.1)',
                                    fontSize: '1.3rem',
                                  }}
                                >
                                  {code}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    display: 'block',
                                    mt: -0.5
                                  }}
                                >
                                  {role}
                                </Typography>
                              </Box>

                              {/* Espace flexible */}
                              <Box sx={{ flex: 1 }} />

                              {/* Solde */}
                              <Box sx={{ 
                                textAlign: 'right', 
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <Typography 
                                  variant="h5"
                                  sx={{ 
                                    fontWeight: 700,
                                    color: data.stock >= 0 ? theme.palette.info.main : theme.palette.error.main,
                                    fontSize: '1.6rem',
                                    lineHeight: 1
                                  }}
                                >
                                  {formatDuration(data.stock)}
                                </Typography>
                              </Box>

                              {/* Bouton détails */}
                              <Button 
                                size="small" 
                                onClick={() => handleToggleCode(key)}
                                sx={{ 
                                  minWidth: 'auto',
                                  p: '4px 8px',
                                  backgroundColor: 'background.paper',
                                  '&:hover': {
                                    backgroundColor: 'background.default'
                                  }
                                }}
                              >
                                {expandedCodes[key] ? 'Réduire' : 'Détails'}
                              </Button>
                            </Box>

                            {/* Section dépliable avec dotation, consommation et détails */}
                            {expandedCodes[key] && (
                              <Box sx={{ mt: 2 }}>
                                {/* Dotation et consommation */}
                                <Box sx={{ 
                                  display: 'flex',
                                  gap: 2,
                                  mb: 2,
                                  p: 1.5,
                                  backgroundColor: theme.palette.background.default,
                                  borderRadius: 1
                                }}>
                                  {[
                                    { 
                                      label: 'Dotation', 
                                      value: formatDuration(data.dotation),
                                      color: theme.palette.success.main,
                                    },
                                    { 
                                      label: 'Consommation', 
                                      value: formatDuration(data.consommation),
                                      color: theme.palette.warning.main,
                                    }
                                  ].map((item, i) => (
                                    <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                                      <Typography 
                                        variant="caption" 
                                        display="block" 
                                        sx={{ 
                                          color: item.color,
                                          fontWeight: 500
                                        }}
                                      >
                                        {item.label}
                                      </Typography>
                                      <Typography 
                                        variant="body1" 
                                        sx={{ 
                                          fontWeight: 600,
                                          color: item.color
                                        }}
                                      >
                                        {item.value}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>

                                {/* Table des détails */}
                                <Table 
                                  size="small" 
                                  sx={{ 
                                    '& td, & th': { 
                                      padding: '6px 8px', 
                                      fontSize: '0.85rem',
                                      borderColor: theme.palette.divider
                                    },
                                    '& th': {
                                      backgroundColor: theme.palette.background.default,
                                      fontWeight: 600
                                    },
                                    '& tr:nth-of-type(even)': {
                                      backgroundColor: theme.palette.action.hover
                                    }
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Membre</TableCell>
                                      <TableCell align="right">Dot.</TableCell>
                                      <TableCell align="right">Cons.</TableCell>
                                      <TableCell align="right">Solde</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {Object.entries(data.users).map(([userId, userData], i) => (
                                      <TableRow key={i}>
                                        <TableCell>{userData.full_name}</TableCell>
                                        <TableCell align="right">{formatDuration(userData.dotation)}</TableCell>
                                        <TableCell align="right">{formatDuration(userData.consommation)}</TableCell>
                                        <TableCell align="right">{formatDuration(userData.stock)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Box>

          {/* Section des codes variables */}
          <Box sx={{ mt: 2 }}>
            {showVariableCodes && (
              <Grid container spacing={1}>
                {Object.keys(stockData)
                  .filter(key => {
                    const [code, role] = key.split('-');
                    const releveCode = releveCodes.find(r => r.code === code && r.role === role);
                    return releveCode?.particularity === 'variable';
                  })
                  .map((key, index) => {
                    const [code, role] = key.split('-');
                    const data = stockData[key];
                    const color = (releveCodes.find(r => r.code === code && r.role === role) || {}).color;

                    // Afficher tous les membres ayant un mandat actif, même sans consommation
                    // On trie par nom pour l'affichage
                    const usersList = Object.values(data.users)
                      .sort((a, b) => a.full_name.localeCompare(b.full_name));

                    return (
                      <Grid item xs={12} md={4} lg={3} key={index}>
                        <Paper sx={{ 
                          p: 1.5,
                          height: '100%',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            height: '100%',
                          }}>
                            {/* En-tête avec le code et la consommation */}
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: color + '15',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              position: 'relative',
                            }}>
                              {/* Code et rôle */}
                              <Box>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: color,
                                    textShadow: '0px 1px 2px rgba(0,0,0,0.1)',
                                    fontSize: '1.3rem',
                                  }}
                                >
                                  {code}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    display: 'block',
                                    mt: -0.5
                                  }}
                                >
                                  {role}
                                </Typography>
                              </Box>

                              {/* Espace flexible */}
                              <Box sx={{ flex: 1 }} />

                              {/* Consommation */}
                              <Box sx={{ 
                                textAlign: 'right', 
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <Typography 
                                  variant="h5"
                                  sx={{ 
                                    fontWeight: 700,
                                    color: theme.palette.warning.main,
                                    fontSize: '1.6rem',
                                    lineHeight: 1
                                  }}
                                >
                                  {formatDuration(data.consommation)}
                                </Typography>
                              </Box>

                              {/* Bouton détails */}
                              <Button 
                                size="small" 
                                onClick={() => handleToggleCode(key)}
                                sx={{ 
                                  minWidth: 'auto',
                                  p: '4px 8px',
                                  backgroundColor: 'background.paper',
                                  '&:hover': {
                                    backgroundColor: 'background.default'
                                  }
                                }}
                              >
                                {expandedCodes[key] ? 'Réduire' : 'Détails'}
                              </Button>
                            </Box>

                            {/* Section dépliable avec consommation par utilisateur */}
                            {expandedCodes[key] && (
                              <Box sx={{ mt: 2 }}>
                                {/* Table des détails */}
                                <Table 
                                  size="small" 
                                  sx={{ 
                                    '& td, & th': { 
                                      padding: '6px 8px', 
                                      fontSize: '0.85rem',
                                      borderColor: theme.palette.divider
                                    },
                                    '& th': {
                                      backgroundColor: theme.palette.background.default,
                                      fontWeight: 600
                                    },
                                    '& tr:nth-of-type(even)': {
                                      backgroundColor: theme.palette.action.hover
                                    }
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Membre</TableCell>
                                      <TableCell align="right">Consommation</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {usersList.map((userData, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{userData.full_name}</TableCell>
                                        <TableCell align="right">{formatDuration(userData.consommation)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Box>
        </Grid>

        {/* Panel des graphiques */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} id="panel-graphiques">
            <Typography variant="h6" gutterBottom>Analyses et statistiques</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: { xs: '300px', sm: '350px', md: '400px' },
                  minHeight: '250px'
                }}>
                  <Typography variant="subtitle1" gutterBottom>Consommation du secteur</Typography>
                  <Bar data={activityData} options={{ 
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                      padding: {
                        bottom: 25 // Ajouter de l'espace en bas
                      }
                    },
                    scales: {
                      y: {
                        ticks: {
                          color: theme.palette.text.primary,
                          callback: (value) => Math.round(value)
                        },
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        ticks: {
                          color: theme.palette.text.primary,
                          maxRotation: 45,
                          minRotation: 45
                        },
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: theme.palette.text.primary,
                          boxWidth: 10,
                          padding: 10
                        }
                      }
                    }
                  }} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: { xs: '300px', sm: '350px', md: '400px' },
                  minHeight: '250px'
                }}>
                  <Typography variant="subtitle1" gutterBottom>Consommation par activité</Typography>
                  <Bar data={consommationParActiviteData} options={{ 
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                      padding: {
                        bottom: 25 // Ajouter de l'espace en bas
                      }
                    },
                    scales: {
                      y: {
                        ticks: {
                          color: theme.palette.text.primary,
                          callback: (value) => Math.round(value)
                        },
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        ticks: {
                          color: theme.palette.text.primary,
                          maxRotation: 45,
                          minRotation: 45
                        },
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: theme.palette.text.primary,
                          boxWidth: 10,
                          padding: 10
                        }
                      }
                    }
                  }} />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Faits marquants en bas */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} id="faits-marquants">
            <Typography variant="h6" gutterBottom>Faits Marquants</Typography>
            <Grid container spacing={2}>
              {Object.entries(
                dataWithUsers
                  .filter(p => {
                    // Filtrer selon la période sélectionnée
                    const pointageDate = new Date(p.date);
                    if (selectedPeriod === 'rolling') {
                      const currentDate = new Date();
                      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 11, 1);
                      return p.on_report && pointageDate >= startDate && pointageDate <= currentDate;
                    } else {
                      const year = parseInt(selectedPeriod);
                      return p.on_report && pointageDate.getFullYear() === year;
                    }
                  })
                  .reduce((groups, pointage) => {
                    const activityName = pointage.activity?.name || 'Sans activité';
                    if (!groups[activityName]) groups[activityName] = [];
                    groups[activityName].push(pointage);
                    return groups;
                  }, {})
              ).map(([activityName, groupedPointages]) => (
                <Grid item xs={12} md={4} lg={3} key={activityName}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ 
                      mb: 1, 
                      fontWeight: 600,
                      backgroundColor: (groupedPointages[0]?.activity?.color || 'grey.300') + '20',
                      p: 1,
                      borderRadius: 1,
                    }}>
                      {activityName}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {[...groupedPointages]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((pointage) => (
                          <Box key={pointage.id} sx={{
                            p: 1,
                            borderLeft: '3px solid',
                            borderColor: pointage.activity?.color || 'grey.300',
                            backgroundColor: 'background.paper',
                            borderRadius: '0 4px 4px 0',
                            boxShadow: 1
                          }}>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {format(new Date(pointage.date), 'dd/MM/yyyy')}
                            </Typography>
                            <Typography variant="body2">
                              {pointage.description}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;